import type { MouseEvent } from 'react';

/**
 * Whether a click on a card is the CARD's — landed on its background — rather than one of its controls'.
 * A card that is a tap target as a whole runs its main action only for the card's own clicks, so a click
 * on a button inside it runs once, not twice.
 *
 * Three ways a control's click reaches the card looking like a background click, each ruled out here:
 * - A portaled modal (the read-along) bubbles through React to the card, though its DOM is elsewhere.
 * - The target is inside a link or button.
 * - A loading `Button` / `IconButton` is `pointer-events: none`, so the browser hit-tests THROUGH it and
 *   the click lands on the card behind. So a click whose point lies inside any control's box is the
 *   control's, even when the control was not the target.
 */
export function isCardBackgroundClick(e: MouseEvent<HTMLElement>): boolean {
  const card = e.currentTarget;
  if (!card.contains(e.target as Node)) return false;
  if ((e.target as HTMLElement).closest('a,button')) return false;
  for (const control of Array.from(card.querySelectorAll('a,button'))) {
    const r = control.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue;
    if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) return false;
  }
  return true;
}

/** Runs `run` for the card's own clicks only. */
export const fromCardBackground = (run: () => void) => (e: MouseEvent<HTMLElement>) => {
  if (isCardBackgroundClick(e)) run();
};
